import { useQuery } from '@tanstack/react-query';
import type {
  AnalyticsOverview,
  AnalyticsRange,
  ContractorBenchmark,
  PeerBenchmark,
} from '@munifocus/contracts-gov';
import { api } from './api-client';

/**
 * The council dashboard and its benchmarks. Gated server-side on
 * `analytics:read` at the municipality (or `analytics:benchmark` for the
 * contractor's named comparison), and refused for a municipality that has not
 * turned analytics on.
 */
export const analyticsKeys = {
  overview: (lg: string, r: AnalyticsRange) => ['analytics', 'overview', lg, r.from, r.to] as const,
  peers: (lg: string, r: AnalyticsRange) => ['analytics', 'peers', lg, r.from, r.to] as const,
  contractor: (r: AnalyticsRange) => ['analytics', 'contractor', r.from, r.to] as const,
};

/**
 * Where in the account's reach analytics is turned on — for the rail, which
 * offers the dashboard only once this says so.
 */
export function useAnalyticsAvailability(enabled: boolean) {
  return useQuery({
    queryKey: ['analytics', 'availability'] as const,
    queryFn: async () =>
      (await api.get<{ localGovernmentIds: string[] }>('/analytics/availability')).data,
    enabled,
    // A municipality switches a module on rarely; the rail need not notice within seconds.
    staleTime: 5 * 60_000,
    meta: { silent: true },
  });
}

export function useAnalyticsOverview(localGovernmentId: string | undefined, range: AnalyticsRange) {
  return useQuery({
    queryKey: analyticsKeys.overview(localGovernmentId ?? '', range),
    queryFn: async () =>
      (
        await api.get<AnalyticsOverview>(
          `/analytics/local-governments/${localGovernmentId}/overview`,
          { params: range },
        )
      ).data,
    enabled: !!localGovernmentId,
    retry: false,
  });
}

export function usePeerBenchmark(localGovernmentId: string | undefined, range: AnalyticsRange) {
  return useQuery({
    queryKey: analyticsKeys.peers(localGovernmentId ?? '', range),
    queryFn: async () =>
      (
        await api.get<PeerBenchmark>(
          `/analytics/local-governments/${localGovernmentId}/benchmark`,
          {
            params: range,
          },
        )
      ).data,
    enabled: !!localGovernmentId,
    retry: false,
  });
}

export function useContractorBenchmark(range: AnalyticsRange, enabled: boolean) {
  return useQuery({
    queryKey: analyticsKeys.contractor(range),
    queryFn: async () =>
      (await api.get<ContractorBenchmark>('/analytics/benchmark', { params: range })).data,
    enabled,
  });
}

/** Fetch the CSV with the session's credentials and hand it to the browser. */
export async function downloadAnalyticsCsv(
  localGovernmentId: string,
  range: AnalyticsRange,
  locale: 'en' | 'fr',
): Promise<void> {
  const res = await api.get<Blob>(`/analytics/local-governments/${localGovernmentId}/export`, {
    params: { ...range, locale },
    responseType: 'blob',
  });
  const disposition = String(res.headers['content-disposition'] ?? '');
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'analytics.csv';
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** `YYYY-MM-DD` of today minus `days`, in the browser's calendar. */
export function daysAgo(days: number, from: Date = new Date()): string {
  const d = new Date(from);
  d.setDate(d.getDate() - days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** A rate as a whole percentage, or an em dash when there is nothing to divide. */
export function formatRate(rate: number | null, language: string): string {
  if (rate === null) return '—';
  return new Intl.NumberFormat(language.startsWith('fr') ? 'fr-CA' : 'en-CA', {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(rate);
}

export function formatNumber(value: number | null, language: string, currency = false): string {
  if (value === null) return '—';
  return new Intl.NumberFormat(language.startsWith('fr') ? 'fr-CA' : 'en-CA', {
    ...(currency ? { style: 'currency', currency: 'CAD' } : { maximumFractionDigits: 1 }),
  }).format(value);
}
